import isEmpty from 'lodash/isEmpty';
import { useAppendDraftComment } from 'pages/WellGuardian/WIR/hooks';
import { getWirCreatedReactiveScopeData } from 'pages/WellGuardian/WIR/utils';
import {
  WIRCreatedSections,
  WIRCreateJourneyStepsKeys,
  WIRStageGate2Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useSelectUser } from '../../../../redux/User/selectors';
import { useCountryState, usePrimaveraJobId, useSelectedWIR } from '../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { CountryData, WIR, WIRPhaseTypes } from 'types/wellGuardian';

import { getWirCreatedPhaseName } from '../utils/getSectionData';

export const useSectionInitialRequestInformationData = () => {
  const wirFromState = useSelectedWIR();
  const user = useSelectUser();
  const userName = useMemo(() => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(), [user]);
  const { appendDraftComment } = useAppendDraftComment();
  const countryState = useCountryState();
  const jobId = usePrimaveraJobId();

  const initialRequestInformationPayload = useMemo(() => {
    const { phase, initialRequestInformation } = getWirCreatedReactiveScopeData(
      wirFromState,
      countryState,
    );
    const originatorId = initialRequestInformation?.originatorId;
    const originatorName = initialRequestInformation?.originatorName;
    const interventionTitle = initialRequestInformation?.interventionTitle;
    const isUnplannedReactiveScope = wirFromState?.isUnplannedReactiveScope;
    const expectedEngineeringTime = Number(wirFromState?.expectedEngineeringTime);
    const objectives = initialRequestInformation?.objectives;
    const interventionDate =
      initialRequestInformation?.interventionDate ?? new Date().toISOString();
    const interventionType = initialRequestInformation?.interventionType;
    const interventionComplexity = initialRequestInformation?.interventionComplexity;
    const completedDate = initialRequestInformation?.completedDate;
    const assignedTo = initialRequestInformation?.assignedTo;
    const comments = appendDraftComment(initialRequestInformation?.comments || []);
    const attachments = initialRequestInformation?.attachments;
    const initialBudget = initialRequestInformation?.initialBudget;
    const productGains = initialRequestInformation?.productGains;
    const productionSafeguarded = initialRequestInformation?.productionSafeguarded;
    const expectedInterventionDuration = initialRequestInformation?.expectedInterventionDuration;
    const ghgEmission = initialRequestInformation?.ghgEmission;
    const sectionCompleteConfirmation = initialRequestInformation?.sectionCompleteConfirmation;
    const budgetClassification = initialRequestInformation?.budgetClassification;
    const budgetClassJustification = initialRequestInformation?.budgetClassJustification;
    const well = wirFromState?.well;
    const well_id = wirFromState?.wellId;
    const priority = initialRequestInformation?.priority;
    const scope = initialRequestInformation?.scope;

    const isTickedNonCG =
      !isEmpty(expectedInterventionDuration?.toString()) &&
      !isEmpty(initialBudget) &&
      !isEmpty(budgetClassification);

    const isTickedAll =
      !isEmpty(originatorName) &&
      !isEmpty(interventionTitle) &&
      !isEmpty(objectives) &&
      !isEmpty(interventionDate) &&
      !isEmpty(interventionType) &&
      !isEmpty(interventionComplexity) &&
      !isEmpty(productGains) &&
      !isEmpty(productionSafeguarded) &&
      !isEmpty(ghgEmission);

    const isTicked =
      countryState === CountryData.congo ? isTickedAll : isTickedAll && isTickedNonCG;

    return {
      originatorId,
      originatorName,
      interventionTitle,
      isUnplannedReactiveScope,
      expectedEngineeringTime,
      objectives,
      interventionDate,
      interventionType,
      interventionComplexity,
      isTicked,
      well,
      well_id,
      jobId,
      assignedTo,
      comments,
      attachments,
      initialBudget,
      productGains,
      productionSafeguarded,
      expectedInterventionDuration,
      ghgEmission,
      sectionCompleteConfirmation,
      budgetClassification,
      budgetClassJustification,
      lastUpdatedDate: new Date().toISOString(),
      completedDate: isTicked ? new Date().toISOString() : completedDate,
      completedBy: isTicked ? userName : initialRequestInformation?.completedBy,
      phase,
      priority,
      scope,
    };
  }, [wirFromState, userName, appendDraftComment, countryState, jobId]);

  const saveInitialRequestInformation = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      const phaseName = getWirCreatedPhaseName(countryState);
      if (
        wirFromState?.wirNumber &&
        (section === WIRCreatedSections.initialRequestInformation ||
          section === WIRStageGate2Sections.initialRequestInformation)
      ) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRInitialRequestInformation(
            wirFromState?.wirNumber,
            initialRequestInformationPayload,
            initialRequestInformationPayload.phase === WIRCreateJourneyStepsKeys.REACTIVE_SCOPE
              ? WIRPhaseTypes.REACTIVE_SCOPE
              : phaseName,
            countryState,
          ),
        );
      }
    },
    [wirFromState, initialRequestInformationPayload, countryState],
  );

  return {
    saveInitialRequestInformation,
    initialRequestInformationPayload,
  };
};
